import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, RotateCw, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Reusable image cropper with zoom + pan. Outputs a square base64 PNG at outputSize.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onSave: (dataUrl: string) => void
 *   file: File
 *   outputSize: number (default 400)
 *   shape: "circle" | "square" (preview mask)
 *   title: string
 */
const ImageCropperModal = ({ open, onClose, onSave, file, outputSize = 400, shape = "circle", title = "Adjust Image" }) => {
  const [src, setSrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const imgRef = useRef(null);
  const boxSize = 280; // preview box in px

  useEffect(() => {
    if (!file) { setSrc(null); return; }
    const reader = new FileReader();
    reader.onloadend = () => setSrc(reader.result);
    reader.readAsDataURL(file);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [file]);

  const onMouseDown = (e) => {
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPan({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
  };
  const onMouseUp = () => setDragging(false);

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    setDragging(true);
    dragStart.current = { x: t.clientX, y: t.clientY, px: pan.x, py: pan.y };
  };
  const onTouchMove = (e) => {
    if (!dragging || e.touches.length !== 1) return;
    const t = e.touches[0];
    const dx = t.clientX - dragStart.current.x;
    const dy = t.clientY - dragStart.current.y;
    setPan({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
  };
  const onTouchEnd = () => setDragging(false);

  const handleSave = () => {
    if (!src || !imgRef.current) return;
    const img = imgRef.current;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    // The image is rendered at object-fit: cover inside boxSize x boxSize.
    // Base scale so image covers the box:
    const baseScale = Math.max(boxSize / natW, boxSize / natH);
    const effectiveScale = baseScale * zoom;
    const renderedW = natW * effectiveScale;
    const renderedH = natH * effectiveScale;
    // Box center in rendered-image coords (before pan):
    // Image is centered in box, so image top-left = (boxSize - renderedW)/2 + pan.x
    const imgLeft = (boxSize - renderedW) / 2 + pan.x;
    const imgTop = (boxSize - renderedH) / 2 + pan.y;
    // We want to extract the box area from the rendered image, converted back to source coords:
    const srcX = (0 - imgLeft) / effectiveScale;
    const srcY = (0 - imgTop) / effectiveScale;
    const srcW = boxSize / effectiveScale;
    const srcH = boxSize / effectiveScale;
    // Draw to output canvas
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outputSize, outputSize);
    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputSize, outputSize);
    const dataUrl = canvas.toDataURL("image/png", 0.92);
    onSave(dataUrl);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white border-gray-200 text-gray-800" data-testid="image-cropper-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div
            className="relative mx-auto bg-gray-100 overflow-hidden select-none"
            style={{
              width: boxSize,
              height: boxSize,
              borderRadius: shape === "circle" ? "50%" : "12px",
              cursor: dragging ? "grabbing" : "grab",
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {src && (
              <img
                ref={imgRef}
                src={src}
                alt="preview"
                draggable={false}
                className="absolute"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: "center",
                  transition: dragging ? "none" : "transform 0.08s linear",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button type="button" size="sm" variant="outline" onClick={() => setZoom(z => Math.max(1, +(z - 0.1).toFixed(2)))} data-testid="cropper-zoom-out">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <input
              type="range"
              min="1"
              max="3"
              step="0.05"
              value={zoom}
              onChange={e => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-emerald-600"
              data-testid="cropper-zoom-slider"
            />
            <Button type="button" size="sm" variant="outline" onClick={() => setZoom(z => Math.min(3, +(z + 0.1).toFixed(2)))} data-testid="cropper-zoom-in">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} data-testid="cropper-reset" title="Reset">
              <RotateCw className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-[11px] text-gray-400 text-center">Drag to reposition • Use slider to zoom</p>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button type="button" onClick={handleSave} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="cropper-save-btn">
              <Check className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropperModal;
